import React from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import espectacularImg from '../assets/images/productos/EspectacularCompress.png';
import espectacularImg2 from '../assets/images/productos/MesaRender.png';
import espectacularImg3 from '../assets/images/productos/MesaRender2.png';

import font from '../assets/fonts/Brokenbrush.ttf';

const Hero = ({ currentView, setCurrentView }) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = React.useState(0);

  const images = [espectacularImg, espectacularImg2, espectacularImg3];

  // Auto-play functionality
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 5000) 

    return () => clearInterval(interval);
  }, [images.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleDiscoverMore = () => {
    navigate('/marcas');
  };

  return (
    <section id="inicio" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Cargar fuente personalizada */}
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      
      {/* Imagen principal como fondo de toda la sección con filtros mejorados */}
      <div className="absolute inset-0 w-full h-full z-0 overflow-hidden">
        <div className="relative w-full h-full">
          {images.map((img, idx) => {
            // Solo renderiza la imagen actual y la anterior
            if (idx !== currentSlide && idx !== (currentSlide - 1 + images.length) % images.length) return null;
            const isCurrent = idx === currentSlide;
            return (
              <img
                key={idx}
                src={img}
                alt="Espectacular"
                className={`object-cover w-full h-full absolute top-0 left-0 transition-transform duration-700 ease-in-out ${
                  isCurrent
                    ? 'z-20 translate-x-0 opacity-100'
                    : 'z-10 translate-x-0 opacity-100'
                } ${isCurrent ? 'slide-in-from-right' : ''}`}
                style={{
                  minHeight: '100%',
                  minWidth: '100%',
                  objectPosition: 'center 80%',
                  filter: 'brightness(0.6) contrast(1.1) saturate(1.2)',
                }}
              />
            );
          })}
          {/* Overlays */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/60 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/20 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/10 to-black/40 pointer-events-none" />
        </div>
      </div>

      {/* Navigation Arrows con mejor contraste */}
      <button 
        onClick={prevSlide}
        className="absolute left-6 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-black/50 transition-all duration-300 border border-white/20"
      >
        <ChevronLeft className="w-6 h-6 text-white drop-shadow-lg" />
      </button>
      
      <button 
        onClick={nextSlide}
        className="absolute right-6 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-black/50 transition-all duration-300 border border-white/20"
      >
        <ChevronRight className="w-6 h-6 text-white drop-shadow-lg" />
      </button>      {/* Main Content */}
      <div className="relative z-10 w-full">
        {/* Resto del contenido principal */}
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[70vh] md:min-h-[80vh] lg:min-h-[90vh]">{/* Right Side - Content con mejor legibilidad */}
            <div className="text-center lg:text-left">
              
              {/* Main Headline con sombras mejoradas */}
              <div className="mb-8">
                <div className="mb-0">
                  <h1 className="text-5xl md:text-7xl lg:text-6xl font-black text-white leading-tight drop-shadow-2xl" style={{ fontFamily: "'Brokenbrush'", lineHeight: 1.05 }}>
                    <AnimatedHeadline text="LA PRACTICIDAD" />
                    <br />
                    <AnimatedHeadline text="EN TU MESA" />
                  </h1>
                </div>
                <div className="flex items-center space-x-2 justify-center lg:justify-start -mt-2">
                  <span
                    className="text-2xl md:text-3xl text-white font-light drop-shadow-lg tracking-wide"
                    style={{ fontFamily: 'Brokenbrush', animation: 'bounce-in 0.7s 0.2s cubic-bezier(.68,-0.55,.27,1.55) both' }}
                  >
                    CON
                  </span>
                  <div className="relative flex items-center">
                    <svg
                      viewBox="0 0 340 60"
                      className="w-[260px] h-14 md:w-[320px] md:h-16 lg:w-[400px] lg:h-20 drop-shadow-2xl"
                      style={{
                        filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.3)) drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                      }}
                    >
                      {/* Texto "Campo" */}
                      <text
                        x="10"
                        y="50"
                        fontSize="48"
                        fontWeight="900"
                        fill="#ea580c"
                        stroke="#ffffff"
                        strokeWidth="0.5"
                        fontFamily="'Brokenbrush', system-ui, -apple-system, sans-serif"
                        style={{
                          textShadow: '1px 1px 2px rgba(0,0,0,0.4)',
                          fontFamily: 'Brokenbrush, system-ui, -apple-system, sans-serif',
                          animation: 'bounce-in 0.7s 0.4s cubic-bezier(.68,-0.55,.27,1.55) both'
                        }}
                      >
                        Campo
                      </text>
                      {/* Texto "Fresco" */}
                      <text
                        x="145"
                        y="50"
                        fontSize="48"
                        fontWeight="900"
                        fill="#22c55e"
                        stroke="#ffffff"
                        strokeWidth="0.5"
                        fontFamily="'Brokenbrush', system-ui, -apple-system, sans-serif"
                        style={{
                          textShadow: '1px 1px 2px rgba(0,0,0,0.4)',
                          fontFamily: 'Brokenbrush, system-ui, -apple-system, sans-serif',
                          animation: 'bounce-in 0.7s 0.6s cubic-bezier(.68,-0.55,.27,1.55) both'
                        }}
                      >
                        Fresco
                      </text>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Product Feature con mejor contraste */}
              {/* <div className="mb-8">
                <div className="inline-flex items-center space-x-3 bg-black/30 backdrop-blur-md rounded-full px-6 py-3 mb-4 border border-white/20 shadow-xl">
                  <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-lg">🌾</span>
                  </div>
                  <span className="text-white font-semibold drop-shadow-md">Los mejores GRANOS Y SEMILLAS</span>
                </div>
                
                <p className="text-lg md:text-xl text-white/95 max-w-md mx-auto lg:mx-0 drop-shadow-lg">
                  Ideal para freír, asar, guisar
                </p>
              </div> */}              {/* CTA Button mejorado */}
              <div className="mb-12">
                <button 
                  onClick={handleDiscoverMore}
                  className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-lg px-8 py-4 rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300 uppercase tracking-wide border-2 border-yellow-500 hover:border-yellow-300"
                >
                  Descubre más
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator mejorado */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-6 h-6 text-white/80 drop-shadow-lg" />
      </div>

      {/* Dots Indicator con mejor contraste */}
      <div className="absolute bottom-6 right-6 flex space-x-2">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              currentSlide === index 
                ? 'bg-yellow-400 shadow-lg scale-110' 
                : 'bg-white/60 shadow-md hover:bg-white/80'
            }`}
          />
        ))}
      </div>

    </section>
  );
};

// Componente para animar letra por letra
const AnimatedHeadline = ({ text }) => {
  return (
    <span>
      {text.split('').map((char, i) => (
        <span
          key={i}
          className="inline-block animate-fadeinup-letter"
          style={{ animationDelay: `${i * 0.045}s` }}
        >
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </span>
  );
};

const styles = `
  @font-face {
    font-family: 'Brokenbrush';
    src: url('${font}') format('truetype');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }
  @keyframes fadeinup-letter {
    0% { opacity: 0; transform: translateY(40px); }
    60% { opacity: 1; transform: translateY(-8px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  .animate-fadeinup-letter {
    animation: fadeinup-letter 0.7s cubic-bezier(0.23, 1, 0.32, 1) both;
    display: inline-block;
  }
  @keyframes bounce-in {
    0% {
      opacity: 0;
      transform: scale(0.3) translateY(100px);
    }
    50% {
      opacity: 1;
      transform: scale(1.05) translateY(-10px);
    }
    70% {
      transform: scale(0.95) translateY(5px);
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
  .slide-in-from-right {
    animation: slideInFromRight 0.7s cubic-bezier(0.23, 1, 0.32, 1) both;
  }
  @keyframes slideInFromRight {
    0% {
      transform: translateX(100%);
      opacity: 0.7;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

export default Hero;